import { useEffect, useMemo, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { CustomButton } from "../../CustomButton";
import { BubbleIcon } from "components/images";
import { BubbleLegendBlock } from "./BubbleLegendBlock";
import { BaseModal } from "../../BaseModal";
import { Chart } from "react-chartjs-2";
import { useDebounceValue, useStateWithSearchParams } from "../../../../hooks";
import { useLazyGetBubbleChartQuery } from "../../../../api";
import { QUERY_SEPARATOR } from "../../../../constant";
import { IndicatorTimeframe, Order } from "../../../../types";
import { KeyRoles } from "../../../../types/enums";
import { keyRolesItems } from "../../../../types/genereal";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  BubbleController,
  type ChartData,
  ChartOptions,
  type ChartTypeRegistry,
} from "chart.js";
import { ReuseTooltip } from "../../ReuseTooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  BubbleController,
);

export type BubbleChartItem = {
  role: KeyRoles;
  expected: number;
  actual: number;
  weight: number;
  label: string;
  color: string;
};

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  limit?: string;
  date?: string;
  period?: string;
};

export const BubbleChartBtn = ({
  isAccounts,
  id,
  isDisabled,
  isNotActiveRole,
}: {
  isAccounts?: boolean;
  id: string;
  isDisabled: boolean;
  isNotActiveRole: boolean;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const chartRef = useRef<ChartJS>(null);
  const currentRole = useRef<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("");

  useEffect(() => {
    currentRole.current = selectedRole;
  }, [selectedRole]);

  useEffect(() => {
    if (!isOpen) {
      const tooltipEl = document.getElementById("custom-tooltip");
      if (tooltipEl) tooltipEl.remove();

      const bubbleContainer = document.getElementById("bubble-container");
      if (bubbleContainer) bubbleContainer.remove();
    }
  }, [isOpen]);

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    industries: undefined,
    regions: undefined,
    sizes: undefined,
    search: undefined,
    page: undefined,
    per_page: undefined,
    selected: undefined,
    limit: undefined,
    date: undefined,
    period: undefined,
    sort: undefined,
  });

  const searchValueDebounce = useDebounceValue(searchDict.search || "");

  const [getBubbleChart, { data: currentData, isLoading }] =
    useLazyGetBubbleChartQuery();

  const chartData: BubbleChartItem[] = useMemo(() => {
    return (currentData?.roles || []).map((el) => ({
      ...el,
      label: keyRolesItems[el.role].title,
      color: keyRolesItems[el.role].color,
    }));
  }, [currentData]);

  const stackedZonesPlugin = {
    id: "stackedZones",
    beforeDraw: (chart: any) => {
      if (chartData.length === 0) return;

      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;

      const zones: { top: number; right: number }[] = [];

      chartData.forEach(({ expected }) => {
        if (zones.length === 0) {
          zones.push({ top: expected, right: 1.5 });
        } else {
          if (zones[zones.length - 1].top === expected) {
            zones[zones.length - 1].right = zones[zones.length - 1].right + 1;
          } else {
            zones.push({
              top: expected,
              right: zones[zones.length - 1].right + 1,
            });
          }
        }
      });

      zones[zones.length - 1].right += 1;

      let prevTop = 0;
      let prevRight = 0;

      zones.forEach((zone) => {
        const x0 = xScale.getPixelForValue(prevRight);
        const y0 = yScale.getPixelForValue(prevTop);

        const x1 = xScale.getPixelForValue(zone.right);
        const y1 = yScale.getPixelForValue(zone.top);

        ctx.fillStyle = "#F7F7F7";
        ctx.fillRect(x0, y1, x1 - x0, y0 - y1);

        ctx.beginPath();
        ctx.moveTo(x0, y1);
        ctx.lineTo(x1, y1);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#BCBCBC";
        ctx.stroke();

        prevTop = 0;
        prevRight = zone.right;
      });
    },
  };

  const clipZonesPlugin = {
    id: "clipZones",
    beforeDatasetsDraw: (chart: any) => {
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;

      const maxX = xScale.max;
      const maxY = yScale.max;

      const clipX = xScale.getPixelForValue(0);
      const clipY = yScale.getPixelForValue(maxY);
      const clipW = xScale.getPixelForValue(maxX) - clipX;
      const clipH = yScale.getPixelForValue(0) - clipY;

      ctx.save();
      ctx.beginPath();
      ctx.rect(clipX, clipY, clipW, clipH);
      ctx.clip();
    },
    afterDatasetsDraw: (chart: any) => {
      chart.ctx.restore();
    },
  };

  const roleLabels = ["", ...chartData.map((el) => el.label), ""];

  const maxY =
    Number(
      chartData.reduce(
        (acc, { expected }) => (acc > expected ? acc : expected),
        0,
      ),
    ) * 2;

  const handleLegendClick = (role: string) => {
    const chart = chartRef.current;
    if (!chart) return;

    const dataset = chart.data.datasets[0];
    const index = dataset.data.findIndex((d: any) => d.role === role);
    if (index === -1) return;

    if (!role || currentRole.current === role) {
      setSelectedRole("");
      chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
      chart.setActiveElements([]);
      chart.update("none");
      return;
    }

    setSelectedRole(role);

    const meta = chart.getDatasetMeta(0);
    const element = meta.data[index];
    if (!element) return;

    const center = { x: element.x, y: element.y };

    chart.tooltip?.setActiveElements([{ datasetIndex: 0, index }], center);
    chart.setActiveElements([{ datasetIndex: 0, index }]);

    chart.update("none");
  };

  const data: ChartData<keyof ChartTypeRegistry> = {
    datasets: [
      {
        type: "bubble",
        label: "Roles",
        data: [
          {
            role: "empty",
            label: "",
            weight: 0,
            expected: 0,
            actual: 0,
            color: "transparent",
          },
          ...chartData,
        ].map((el) => ({
          x: el.expected,
          y: maxY < el.actual ? maxY : el.actual,
          r: 0,
          role: el.role,
          color: el.color,
          weight: Math.max(el.weight * 0.8, 14),
          actual: el.actual,
        })),
        backgroundColor: "transparent",
        borderColor: "transparent",
        borderWidth: 0,
      },
    ],
  };

  const options: ChartOptions<keyof ChartTypeRegistry> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (context) => {
          const chart = context.chart;
          const tooltip = context.tooltip;

          let tooltipEl = document.getElementById(
            "custom-tooltip",
          ) as HTMLDivElement;
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "custom-tooltip";
            tooltipEl.style.position = "absolute";
            tooltipEl.style.background = "#496389";
            tooltipEl.style.color = "white";
            tooltipEl.style.padding = "8px 12px";
            tooltipEl.style.borderRadius = "4px";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.transform = "translate(-50%, -100%)";
            tooltipEl.style.fontFamily = "Inter, sans-serif";
            tooltipEl.style.display = "none";
            tooltipEl.style.zIndex = "99999";

            document.body.appendChild(tooltipEl);
          }

          if (tooltip.opacity === 0) {
            tooltipEl.style.display = "none";
            return;
          }

          if (tooltip.dataPoints?.length) {
            const dataPoint = tooltip.dataPoints[0];
            const { role } = dataPoint.raw as {
              role?: string;
            };

            const currentData = chartData.find((el) => el.role === role);

            if (!currentData) return;
            const { color, expected, actual, label, weight } = currentData;

            tooltipEl.innerHTML = `
        <div style="display:flex; flex-direction: column; gap:8px;font-size: 12px; line-height: 12px;
         position: relative">
          <div style="display:flex; align-items:center; gap:6px;">
            <span style="width:12px; height:12px; border-radius:50%; background:${color};"></span>
            <span style="font-weight: 500">${label}</span>
          </div>
          <div style="display:flex; justify-content: space-between; gap: 12px;">Expected: <span>${expected}</span></div>
          <div style="display:flex; justify-content: space-between; gap: 12px;">Actual: <span>${actual}</span></div>
          ${
            actual !== expected
              ? `<div style="display:flex; justify-content: space-between;
            gap: 12px;">${actual - expected > 0 ? "Extra" : "Gap"}: <span>${actual - expected}</span></div>`
              : ""
          }
          <div style="position: absolute; width: 0; height: 0; left: 50%; bottom: -14px; transform: translateX(-50%);
            border-left: 6px solid transparent; border-right: 6px solid transparent; border-top: 6px solid #496389; 
            "></div>
        </div>
      `;

            const canvasRect = chart.canvas.getBoundingClientRect();
            tooltipEl.style.left =
              canvasRect.left + window.pageXOffset + tooltip.caretX + "px";
            tooltipEl.style.top =
              canvasRect.top +
              window.pageYOffset +
              tooltip.caretY -
              (actual >= maxY ? 12 : weight + 12) +
              "px";
            tooltipEl.style.display = "block";
          }
        },
      },
    },
    scales: {
      x: {
        type: "category",
        labels: roleLabels.map((label) => label?.split(" ") || ""),
        title: {
          display: false,
        },
        offset: false,
        ticks: {
          color: "#121212",
          font: { size: 12, weight: 600 },
          align: "center",
          maxRotation: 0,
          minRotation: 0,
        },
        border: { display: false },
      },
      y: {
        type: "linear",
        max: maxY,
        offset: false,
        title: {
          display: true,
          text: "Identified Contacts Per Role",
          color: "#121212",
          font: { size: 16, weight: 600 },
        },
        ticks: {
          color: "#121212",
          font: { size: 16, weight: 500 },
          align: "start",
        },
        border: { display: false },
      },
    },
    elements: { point: { hoverRadius: 0 } },
  };

  const openHandler = () => {
    getBubbleChart({
      id,
      isAccounts,
      limit: searchDict.limit ? Number(searchDict?.limit) : undefined,
      size: searchDict.sizes?.split(QUERY_SEPARATOR),
      search: searchValueDebounce || undefined,
      industry: searchDict.industries?.split(QUERY_SEPARATOR),
      region: searchDict.regions?.split(QUERY_SEPARATOR),
      sort: searchDict.sort?.split("-")[0],
      order: searchDict.sort?.split("-")[1] as Order,
      start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
      end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
      indicator_timeframe: searchDict.period
        ? (searchDict.period.split(" ")[1].toLowerCase() as IndicatorTimeframe)
        : undefined,
    })
      .unwrap()
      .then(() => setIsOpen(true));
  };

  const createHtmlPointPlugin = () => ({
    id: "htmlPointPlugin",
    afterDatasetsDraw(chart: any) {
      const { canvas, chartArea } = chart;

      const dataset = chart.data.datasets[0];
      const meta = chart.getDatasetMeta(0);

      let container = document.getElementById(
        "bubble-container",
      ) as HTMLDivElement;
      if (!container) {
        container = document.createElement("div");
        container.id = "bubble-container";
        container.style.position = "absolute";
        container.style.pointerEvents = "none";
        container.style.overflow = "hidden";
        canvas.parentNode!.appendChild(container);
      }

      container.style.left = `${canvas.offsetLeft + chartArea.left}px`;
      container.style.top = `${canvas.offsetTop + chartArea.top}px`;
      container.style.width = `${chartArea.width}px`;
      container.style.height = `${chartArea.height}px`;

      dataset.data.forEach((dataPoint: any, i: number) => {
        const element = meta.data[i];
        if (!element) return;

        const { x, y } = element;
        const { role, color, weight } = dataPoint;

        let el = document.getElementById(`point-${i}`);
        if (!el) {
          el = document.createElement("div");
          el.id = `point-${i}`;
          el.style.position = "absolute";
          el.style.pointerEvents = "none";
          el.style.fontSize = "12px";

          const innerDiv = document.createElement("div");
          el.appendChild(innerDiv);

          container.appendChild(el);
        }

        const width = weight * 2;
        const height = weight * 2;

        const innerDiv = el.firstChild as HTMLElement;

        innerDiv.style.width = `${width}px`;
        innerDiv.style.height = `${height}px`;
        innerDiv.style.background = color;
        innerDiv.style.border = "4px solid white";

        innerDiv.style.borderRadius = "50%";

        innerDiv.style.opacity =
          currentRole.current && role !== currentRole.current ? "0.7" : "1";
        innerDiv.style.cursor = "pointer";

        innerDiv.style.pointerEvents = "auto";
        innerDiv.onmouseenter = (e) => {
          e.stopPropagation();
          requestAnimationFrame(() => {
            handleLegendClick(role);
          });
        };
        innerDiv.onmouseleave = (e) => {
          e.stopPropagation();
          requestAnimationFrame(() => {
            handleLegendClick(role);
          });
        };

        el.style.left = `${canvas.offsetLeft + x - width / 2 - chartArea.left}px`;
        el.style.top = `${canvas.offsetTop + y - height / 2}px`;
      });
    },
  });

  return (
    <>
      <ReuseTooltip
        text={
          (isDisabled || isNotActiveRole) &&
          (isNotActiveRole
            ? "No active Roles found."
            : "Analysis required first.")
        }
        hideChildren={false}
      >
        <CustomButton
          onClick={openHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          customSize="fit"
          fullWidth
          type="button"
          startIcon={<BubbleIcon />}
          loading={isLoading}
          sx={{
            height: "32px",
            borderColor: "#1D3E70",
            minWidth: "162px",
            maxWidth: "162px",
            textWrap: "nowrap",
          }}
          disabled={isDisabled || isNotActiveRole}
        >
          Role Gap Chart
        </CustomButton>
      </ReuseTooltip>
      {isOpen && (
        <BaseModal
          sx={{
            padding: "32px",
            width: "100%",
            maxWidth: "min(1340px, Calc(100vw - 100px))",
          }}
          open={isOpen && !isLoading}
          onClose={() => {
            setSelectedRole("");
            setIsOpen(false);
          }}
          title="Role Gap Chart"
        >
          <Stack gap="24px">
            <Typography variant="body1" color="text.secondary" maxWidth="940px">
              Bubble size reflects the gap between planned and actual contacts,
              scaled by the role’s impact on the buying decision. Color
              represents the role, while opacity indicates its relative
              importance.
            </Typography>
            <Stack flexDirection="row" gap="16px">
              <Stack flexGrow={1} position="relative">
                <Typography
                  variant="body1"
                  sx={{
                    position: "absolute",
                    top: 20,
                    left: 90,
                    color: "#797979",
                    zIndex: 13,
                    lineHeight: "14px",
                    fontWeight: 500,
                    pointerEvents: "none",
                  }}
                >
                  Overrepresented
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    position: "absolute",
                    bottom: 54,
                    left: 90,
                    color: "#797979",
                    zIndex: 13,
                    lineHeight: "14px",
                    fontWeight: 500,
                    pointerEvents: "none",
                  }}
                >
                  Underrepresented
                </Typography>
                <Chart
                  ref={chartRef}
                  type="bubble"
                  data={data}
                  options={options}
                  plugins={[
                    stackedZonesPlugin,
                    clipZonesPlugin,
                    createHtmlPointPlugin(),
                  ]}
                />
              </Stack>

              <BubbleLegendBlock
                roles={chartData}
                selectedRole={selectedRole}
                handleLegendClick={handleLegendClick}
              />
            </Stack>
          </Stack>
        </BaseModal>
      )}
    </>
  );
};
